// angular
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Params } from '@angular/router';
// services
import { StateManagementService } from '@app/+chat/shared/services/state-management.service';
import { PwjToastService } from '@app/core/shared/services/app-toast.service';
// interfaces & enums
import { ITender, ITenderResponse } from '../interfaces/tender.interface';
import {
  IProposalForm,
  IProposalResponse,
} from '../interfaces/proposal.interface';
import { ApiSearchTypesEnum } from '@app/core/shared/enums/api-search-types.enum';

@Injectable()
export class TendersService {
  // states
  public tendersList = signal<ITender[]>([]);
  public allTendersCount = signal<number | null>(null);
  public isTendersListLoading = signal<boolean>(false);
  public tenderDetails = signal<ITender>({});
  public isTenderDetailsLoading = signal<boolean>(false);
  public isHomeTendersLoading = signal<boolean>(false);

  // dependencies
  private readonly _httpClient = inject(HttpClient);
  private readonly _toast = inject(PwjToastService);
  private readonly _stateManagement = inject(StateManagementService);
  constructor() {}

  // get all tenders observable
  public getAllTenders$(params: Params) {
    this.isHomeTendersLoading.set(true);
    return this._httpClient.get<ITenderResponse>(`main/tenders`, {
      params: { search_type: ApiSearchTypesEnum.Tender, ...params },
    });
  }
  // get all tenders
  public getAllTenders(queryParams?: Params) {
    // set is loading to true
    this.isTendersListLoading.set(true);

    // api call
    return this._httpClient
      .get<ITenderResponse>(`main/tenders`, {
        params: {
          ...queryParams,
          search_type: ApiSearchTypesEnum.Tender,
        },
      })
      .subscribe({
        next: res => {
          // customize data
          const customizedData = res?.data?.map((tender: ITender) => ({
            ...tender,
            start_time: tender?.start_time,
            finish_time: tender?.finish_time,
          }));
          this.tendersList.set(customizedData);

          this.allTendersCount.set(res.count);
        },
        error: () => this.isTendersListLoading.set(false),
        complete: () => this.isTendersListLoading.set(false),
      });
  }

  // get tender details
  public getTenderDetails(tenderId: string) {
    // set loading state
    this.isTenderDetailsLoading.set(true);
    return this._httpClient.get<ITender>(`main/tenders/${tenderId}`).subscribe({
      next: res => {
        const customizedData = {
          ...res,
          start_time: res?.start_time,
          finish_time: res?.finish_time,
        };

        this.tenderDetails.set(customizedData);

        // set business id for chat
        res?.business?._id &&
          this._stateManagement.businessId.set(res?.business._id);
      },
      error: () => this.isTenderDetailsLoading.set(false),
      complete: () => this.isTenderDetailsLoading.set(false),
    });
  }

  // post proposal form
  private postProposals(tenderId: string, data: IProposalForm) {
    return this._httpClient.post<IProposalResponse>(
      `main/tenders/${tenderId}/proposals`,
      data
    );
  }

  // submit proposal form data
  public submitProposalsData(tenderId: string, data: IProposalForm) {
    this.postProposals(tenderId, data).subscribe({
      next: res => {
        // success message (toast)
        this._toast.success(
          'toastMessages.submitProposal',
          'toastMessages.successTitle'
        );
      },
      error: error => {
        // auth error message (toast)
        if (error.status === 401) {
          this._toast.error(
            'toastMessages.loginMessage',
            'toastMessages.loginTitle'
          );
        } else {
          this._toast.error('toastMessages.error', 'toastMessages.errorTitle');
        }
      },
      complete: () => null,
    });
  }
}
